import {
  useCreateVisionNoteMutation,
  useDeleteVisionNoteMutation,
  useGetVisionNotesQuery,
  useUpdateVisionNoteMutation,
} from "@/modules/project/services/projectApi";
import { Button } from "@/modules/shadcn/ui/button";
import { Textarea } from "@/modules/shadcn/ui/textarea";
import {
  VISION_FEEDBACK_MAX,
  VISION_NOTES_WARN_CHARS,
  VISION_NOTES_WARN_COUNT,
  VisionProjectNote,
} from "@repo/schema";
import { Loader2, Pencil, Plus, TriangleAlert, X } from "lucide-react";
import { KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  NOTES_ADD,
  NOTES_ADD_PLACEHOLDER,
  NOTES_CANCEL,
  NOTES_EDIT,
  NOTES_EDITED,
  NOTES_EMPTY,
  NOTES_FOOTER,
  NOTES_FORMER_MEMBER,
  NOTES_HELP,
  NOTES_LOAD_FAILED,
  NOTES_OPEN_IMAGE,
  NOTES_REMOVE,
  NOTES_SAVE,
  NOTES_SAVE_FAILED,
  NOTES_TEXT_LABEL,
  NOTES_TITLE,
} from "./copy";
import { SettingsSection } from "./SettingsSection";
import { formatDate, formatRelative } from "@/core/i18n";

interface ProjectNotesSectionProps {
  projectId: number;
  /** Close the dialog and select this image on the label page. */
  onOpenTask: (taskId: number) => void;
  disabled?: boolean;
}

/**
 * The project's Robopipe Vision notes: every feedback turn anyone has sent,
 * verbatim, plus notes added here by hand; oldest first — exactly the list
 * (and order) the model receives. Editing a note keeps its place.
 *
 * Adding, editing and removing are immediate rather than part of the dialog's
 * Save: notes are a separate resource, and holding a change until Save would
 * let a Cancel silently undo something the admin thought was done.
 */
export const ProjectNotesSection = ({
  projectId,
  onOpenTask,
  disabled,
}: ProjectNotesSectionProps) => {
  const { t } = useTranslation(["label", "common"]);
  const { data, isLoading, isError } = useGetVisionNotesQuery({ projectId });
  const [createNote] = useCreateVisionNoteMutation();
  const [updateNote] = useUpdateVisionNoteMutation();
  const [deleteNote] = useDeleteVisionNoteMutation();
  const [adding, setAdding] = useState(false);
  const notes = data?.notes ?? [];
  const totalChars = notes.reduce((sum, note) => sum + note.text.length, 0);
  const tooMany =
    notes.length > VISION_NOTES_WARN_COUNT || totalChars > VISION_NOTES_WARN_CHARS;

  return (
    <SettingsSection
      title={t(NOTES_TITLE)}
      help={t(NOTES_HELP)}
      footerHelp={t(NOTES_FOOTER)}
    >
      {tooMany && (
        <p className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <TriangleAlert className="mt-px size-3.5 shrink-0" aria-hidden />
          {t("settings.notes.tooMany", { count: notes.length })}
        </p>
      )}
      {isError ? (
        <p className="text-xs text-destructive">{t(NOTES_LOAD_FAILED)}</p>
      ) : !isLoading && notes.length === 0 ? (
        <p className="rounded-md bg-black/[0.04] px-3 py-2 text-sm text-muted-foreground">
          {t(NOTES_EMPTY)}
        </p>
      ) : (
        <ol className="flex flex-col gap-1.5">
          {notes.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              disabled={disabled}
              onOpenTask={onOpenTask}
              onSave={(text) => updateNote({ projectId, noteId: note.id, text }).unwrap()}
              onRemove={() => void deleteNote({ projectId, noteId: note.id })}
            />
          ))}
        </ol>
      )}
      {!isError &&
        (adding ? (
          <NoteEditor
            initial=""
            placeholder={t(NOTES_ADD_PLACEHOLDER)}
            disabled={disabled}
            onSave={async (text) => {
              await createNote({ projectId, text }).unwrap();
              setAdding(false);
            }}
            onCancel={() => setAdding(false)}
          />
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="self-start"
            disabled={disabled || isLoading}
            onClick={() => setAdding(true)}
          >
            <Plus />
            {t(NOTES_ADD)}
          </Button>
        ))}
    </SettingsSection>
  );
};

/** Equal on a note never edited: both are set by the same insert. */
const isEdited = (note: VisionProjectNote): boolean =>
  new Date(note.updatedAt).getTime() > new Date(note.createdAt).getTime();

const NoteRow = ({
  note,
  disabled,
  onOpenTask,
  onSave,
  onRemove,
}: {
  note: VisionProjectNote;
  disabled?: boolean;
  onOpenTask: (taskId: number) => void;
  onSave: (text: string) => Promise<unknown>;
  onRemove: () => void;
}) => {
  const { t } = useTranslation(["label", "common"]);
  const [editing, setEditing] = useState(false);
  const taskId = note.taskId;

  if (editing) {
    return (
      <li>
        <NoteEditor
          initial={note.text}
          disabled={disabled}
          onSave={async (text) => {
            await onSave(text);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      </li>
    );
  }

  return (
    <li className="flex items-start gap-2 rounded-md bg-black/[0.04] py-2 pr-1 pl-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-sm leading-5 break-words whitespace-pre-wrap">{note.text}</p>
        <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          <span>{note.author?.fullName ?? t(NOTES_FORMER_MEMBER)}</span>
          <span aria-hidden>·</span>
          <time dateTime={note.createdAt} title={formatDate(note.createdAt)}>
            {formatRelative(note.createdAt)}
          </time>
          {isEdited(note) && (
            <>
              <span aria-hidden>·</span>
              <span title={formatDate(note.updatedAt)}>{t(NOTES_EDITED)}</span>
            </>
          )}
          {/* A manual note is not about any image. */}
          {taskId !== null && (
            <>
              <span aria-hidden>·</span>
              <button
                type="button"
                className="underline-offset-2 hover:text-foreground hover:underline"
                onClick={() => onOpenTask(taskId)}
              >
                {t(NOTES_OPEN_IMAGE)}
              </button>
            </>
          )}
        </p>
      </div>
      <div className="flex shrink-0">
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground"
          aria-label={t(NOTES_EDIT)}
          title={t(NOTES_EDIT)}
          disabled={disabled}
          onClick={() => setEditing(true)}
        >
          <Pencil />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground"
          aria-label={t(NOTES_REMOVE)}
          title={t(NOTES_REMOVE)}
          disabled={disabled}
          onClick={onRemove}
        >
          <X />
        </Button>
      </div>
    </li>
  );
};

/**
 * A note's text being written or rewritten, with its own Save / Cancel.
 * `onSave` rejects when the server refuses; the draft then stays put with the
 * error under it.
 */
const NoteEditor = ({
  initial,
  placeholder,
  disabled,
  onSave,
  onCancel,
}: {
  initial: string;
  placeholder?: string;
  disabled?: boolean;
  onSave: (text: string) => Promise<void>;
  onCancel: () => void;
}) => {
  const { t } = useTranslation(["label", "common"]);
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const text = draft.trim();
  const blocked = disabled || saving || text.length === 0 || text === initial;

  const handleSave = async () => {
    if (blocked) return;
    setSaving(true);
    setFailed(false);
    try {
      await onSave(text);
    } catch {
      setFailed(true);
      setSaving(false);
    }
  };

  // Escape abandons the draft, not the whole dialog.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape" || saving) return;
    event.stopPropagation();
    onCancel();
  };

  return (
    <div
      className="flex flex-col gap-1.5 rounded-md bg-black/[0.04] p-2"
      onKeyDown={handleKeyDown}
    >
      <Textarea
        aria-label={t(NOTES_TEXT_LABEL)}
        rows={3}
        maxLength={VISION_FEEDBACK_MAX}
        autoFocus
        value={draft}
        placeholder={placeholder}
        className="bg-white"
        onChange={(event) => setDraft(event.target.value)}
      />
      {failed && <p className="text-xs text-destructive">{t(NOTES_SAVE_FAILED)}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" disabled={saving} onClick={onCancel}>
          {t(NOTES_CANCEL)}
        </Button>
        <Button size="sm" disabled={blocked} onClick={handleSave}>
          {saving && <Loader2 className="animate-spin" aria-hidden />}
          {t(NOTES_SAVE)}
        </Button>
      </div>
    </div>
  );
};
